import { HeaderPage } from "@/components/HeaderPage";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ArrowLeft, Send, Loader2, Users, MapPin, User } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useState, memo } from "react";
import { useForm, useWatch, Controller } from "react-hook-form";
import { useDebounce } from "@/hooks/useDebounce";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  useCreateGeneralNotification,
  useCreateNeighborhoodNotification,
  useCreateNotification,
} from "@/hooks/useNotifications";
import toast from "react-hot-toast";
import { useQueryClient } from "@tanstack/react-query";
import { useGetUsers } from "@/hooks/useUsers";
import { useGetOcurrences } from "@/hooks/useOccurrences";

// ---- Schemas ----

const generalNotificationSchema = z.object({
  title: z
    .string()
    .min(5, "Título deve ter no mínimo 5 caracteres")
    .max(100, "Título deve ter no máximo 100 caracteres"),
  message: z
    .string()
    .min(20, "Mensagem deve ter no mínimo 20 caracteres")
    .max(2000, "Mensagem deve ter no máximo 2000 caracteres"),
});

const neighborhoodNotificationSchema = z.object({
  neighborhood: z.string().min(1, "Bairro é obrigatório"),
  title: z
    .string()
    .min(5, "Título deve ter no mínimo 5 caracteres")
    .max(100, "Título deve ter no máximo 100 caracteres"),
  message: z
    .string()
    .min(20, "Mensagem deve ter no mínimo 20 caracteres")
    .max(2000, "Mensagem deve ter no máximo 2000 caracteres"),
});

const userNotificationSchema = z.object({
  userId: z.string().min(1, "Usuário é obrigatório"),
  type: z.string().min(1, "Tipo é obrigatório"),
  title: z
    .string()
    .min(5, "Título deve ter no mínimo 5 caracteres")
    .max(100, "Título deve ter no máximo 100 caracteres"),
  message: z
    .string()
    .min(20, "Mensagem deve ter no mínimo 20 caracteres")
    .max(2000, "Mensagem deve ter no máximo 2000 caracteres"),
  isUrgent: z.boolean(),
  occurrenceId: z.string().optional(),
  neighborhood: z.string().optional(),
});

type GeneralNotificationForm = z.infer<typeof generalNotificationSchema>;
type NeighborhoodNotificationForm = z.infer<typeof neighborhoodNotificationSchema>;
type UserNotificationForm = z.infer<typeof userNotificationSchema>;

// Tiny counter — only this element re-renders per keystroke, not the whole form
const FieldCounter = memo(({ control, name, max }: { control: any; name: any; max: number }) => {
  const value = useWatch({ control, name });
  return <span className="text-xs text-muted-foreground">{value?.length ?? 0}/{max}</span>;
});

// ---- Sub-components (isolated — re-renders don't cross tab boundaries) ----

const GeneralNotificationTab = memo(({ onDone }: { onDone: () => void }) => {
  const queryClient = useQueryClient();
  const mutation = useCreateGeneralNotification();
  const form = useForm<GeneralNotificationForm>({
    resolver: zodResolver(generalNotificationSchema),
    defaultValues: { title: "", message: "" },
  });

  const handleSubmit = async (data: GeneralNotificationForm) => {
    try {
      await mutation.mutateAsync(data);
      toast.success("Notificação geral criada com sucesso!");
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
      onDone();
    } catch (error) {
      console.error(error);
      toast.error("Erro ao criar notificação geral");
    }
  };

  return (
    <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
        <p className="text-sm text-blue-800">
          <strong>Notificação Geral:</strong> Será enviada para todos os usuários do sistema.
        </p>
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label htmlFor="general-title">Título *</Label>
          <FieldCounter control={form.control} name="title" max={100} />
        </div>
        <Input
          id="general-title"
          placeholder="Digite o título da notificação"
          maxLength={100}
          {...form.register("title")}
        />
        {form.formState.errors.title && (
          <p className="text-sm text-red-500">{form.formState.errors.title.message}</p>
        )}
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label htmlFor="general-message">Mensagem *</Label>
          <FieldCounter control={form.control} name="message" max={2000} />
        </div>
        <Textarea
          id="general-message"
          placeholder="Digite a mensagem da notificação"
          rows={6}
          maxLength={2000}
          {...form.register("message")}
        />
        {form.formState.errors.message && (
          <p className="text-sm text-red-500">{form.formState.errors.message.message}</p>
        )}
      </div>

      <div className="flex justify-end gap-3 pt-4 border-t">
        <Button type="button" variant="outline" className="cursor-pointer" onClick={onDone}>
          Cancelar
        </Button>
        <Button type="submit" disabled={mutation.isPending} className="text-white">
          {mutation.isPending ? (
            <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Enviando...</>
          ) : (
            <><Send className="h-4 w-4 mr-2" />Enviar Notificação</>
          )}
        </Button>
      </div>
    </form>
  );
});

const NeighborhoodNotificationTab = memo(({ onDone }: { onDone: () => void }) => {
  const queryClient = useQueryClient();
  const mutation = useCreateNeighborhoodNotification();
  const form = useForm<NeighborhoodNotificationForm>({
    resolver: zodResolver(neighborhoodNotificationSchema),
    defaultValues: { neighborhood: "", title: "", message: "" },
  });

  const handleSubmit = async (data: NeighborhoodNotificationForm) => {
    try {
      await mutation.mutateAsync(data);
      toast.success("Notificação de bairro criada com sucesso!");
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
      onDone();
    } catch (error) {
      console.error(error);
      toast.error("Erro ao criar notificação de bairro");
    }
  };

  return (
    <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
      <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
        <p className="text-sm text-green-800">
          <strong>Notificação de Bairro:</strong> Será enviada para todos os usuários de um bairro específico.
        </p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="neighborhood">Bairro *</Label>
        <Input id="neighborhood" placeholder="Digite o nome do bairro" {...form.register("neighborhood")} />
        {form.formState.errors.neighborhood && (
          <p className="text-sm text-red-500">{form.formState.errors.neighborhood.message}</p>
        )}
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label htmlFor="neighborhood-title">Título *</Label>
          <FieldCounter control={form.control} name="title" max={100} />
        </div>
        <Input
          id="neighborhood-title"
          placeholder="Digite o título da notificação"
          maxLength={100}
          {...form.register("title")}
        />
        {form.formState.errors.title && (
          <p className="text-sm text-red-500">{form.formState.errors.title.message}</p>
        )}
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label htmlFor="neighborhood-message">Mensagem *</Label>
          <FieldCounter control={form.control} name="message" max={2000} />
        </div>
        <Textarea
          id="neighborhood-message"
          placeholder="Digite a mensagem da notificação"
          rows={6}
          maxLength={2000}
          {...form.register("message")}
        />
        {form.formState.errors.message && (
          <p className="text-sm text-red-500">{form.formState.errors.message.message}</p>
        )}
      </div>

      <div className="flex justify-end gap-3 pt-4 border-t">
        <Button type="button" variant="outline" onClick={onDone}>Cancelar</Button>
        <Button type="submit" disabled={mutation.isPending} className="text-white">
          {mutation.isPending ? (
            <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Enviando...</>
          ) : (
            <><Send className="h-4 w-4 mr-2" />Enviar Notificação</>
          )}
        </Button>
      </div>
    </form>
  );
});

const UserNotificationTab = memo(({ onDone }: { onDone: () => void }) => {
  const queryClient = useQueryClient();
  const mutation = useCreateNotification();
  const [searchUser, setSearchUser] = useState("");
  const [searchOccurrence, setSearchOccurrence] = useState("");

  const debouncedSearchUser = useDebounce(searchUser, 400);
  const debouncedSearchOccurrence = useDebounce(searchOccurrence, 400);

  const { data: usersData } = useGetUsers({ search: debouncedSearchUser });
  const { data: occurrencesData } = useGetOcurrences({ search: debouncedSearchOccurrence, limit: 10 });

  const users = usersData?.data ?? [];
  const occurrences = occurrencesData?.data ?? [];

  const form = useForm<UserNotificationForm>({
    resolver: zodResolver(userNotificationSchema),
    defaultValues: {
      userId: "",
      type: "OCCURRENCE_UPDATE",
      title: "",
      message: "",
      isUrgent: false,
      occurrenceId: "",
      neighborhood: "",
    },
  });

  const handleSubmit = async (data: UserNotificationForm) => {
    try {
      await mutation.mutateAsync({
        userId: data.userId,
        type: data.type,
        title: data.title,
        message: data.message,
        isUrgent: data.isUrgent,
        occurrenceId: data.occurrenceId || undefined,
        neighborhood: data.neighborhood || undefined,
      });
      toast.success("Notificação enviada ao usuário com sucesso!");
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
      onDone();
    } catch (error) {
      console.error(error);
      toast.error("Erro ao enviar notificação ao usuário");
    }
  };

  return (
    <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
      <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-6">
        <p className="text-sm text-orange-800">
          <strong>Notificação de Usuário:</strong> Será enviada para um usuário específico.
        </p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="userId">Usuário *</Label>
        <Controller
          control={form.control}
          name="userId"
          render={({ field }) => (
            <Select value={field.value} onValueChange={field.onChange}>
              <SelectTrigger>
                <SelectValue placeholder="Selecione um usuário" />
              </SelectTrigger>
              <SelectContent>
                <div className="p-2">
                  <Input
                    placeholder="Buscar usuário..."
                    value={searchUser}
                    onChange={(e) => setSearchUser(e.target.value)}
                    className="mb-2"
                  />
                </div>
                {users.map((user: any) => (
                  <SelectItem key={user.id} value={user.id}>
                    {user.email} - {user.profile?.name || "Sem nome"}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        />
        {form.formState.errors.userId && (
          <p className="text-sm text-red-500">{form.formState.errors.userId.message}</p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="type">Tipo *</Label>
        <Controller
          control={form.control}
          name="type"
          render={({ field }) => (
            <Select value={field.value} onValueChange={field.onChange}>
              <SelectTrigger>
                <SelectValue placeholder="Selecione o tipo" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="OCCURRENCE_UPDATE">Atualização de Ocorrência</SelectItem>
                <SelectItem value="CITY_HALL_RESPONSE">Resposta da Prefeitura</SelectItem>
                <SelectItem value="GENERAL_ANNOUNCEMENT">Anúncio Geral</SelectItem>
                <SelectItem value="NEIGHBORHOOD_ANNOUNCEMENT">Anúncio de Bairro</SelectItem>
              </SelectContent>
            </Select>
          )}
        />
        {form.formState.errors.type && (
          <p className="text-sm text-red-500">{form.formState.errors.type.message}</p>
        )}
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label htmlFor="user-title">Título *</Label>
          <FieldCounter control={form.control} name="title" max={100} />
        </div>
        <Input
          id="user-title"
          placeholder="Digite o título da notificação"
          maxLength={100}
          {...form.register("title")}
        />
        {form.formState.errors.title && (
          <p className="text-sm text-red-500">{form.formState.errors.title.message}</p>
        )}
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label htmlFor="user-message">Mensagem *</Label>
          <FieldCounter control={form.control} name="message" max={2000} />
        </div>
        <Textarea
          id="user-message"
          placeholder="Digite a mensagem da notificação"
          rows={6}
          maxLength={2000}
          {...form.register("message")}
        />
        {form.formState.errors.message && (
          <p className="text-sm text-red-500">{form.formState.errors.message.message}</p>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="occurrenceId">ID da Ocorrência (Opcional)</Label>
          <Controller
            control={form.control}
            name="occurrenceId"
            render={({ field }) => (
              <Select value={field.value} onValueChange={field.onChange}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione uma ocorrência" />
                </SelectTrigger>
                <SelectContent>
                  <div className="p-2">
                    <Input
                      placeholder="Buscar por protocolo..."
                      value={searchOccurrence}
                      onChange={(e) => setSearchOccurrence(e.target.value)}
                      className="mb-2"
                    />
                  </div>
                  {occurrences.length === 0 ? (
                    <div className="p-2 text-sm text-gray-500">Nenhuma ocorrência encontrada</div>
                  ) : (
                    occurrences.map((occurrence: any) => (
                      <SelectItem key={occurrence.id} value={occurrence.id}>
                        {occurrence.protocol} - {occurrence.categoryName}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            )}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="user-neighborhood">Bairro (Opcional)</Label>
          <Input id="user-neighborhood" placeholder="Nome do bairro" {...form.register("neighborhood")} />
        </div>
      </div>

      <div className="flex items-center gap-4 p-4 border rounded-lg">
        <Controller
          control={form.control}
          name="isUrgent"
          render={({ field }) => (
            <input
              type="checkbox"
              id="isUrgent"
              checked={field.value}
              onChange={field.onChange}
              className="h-5 w-5 rounded border-gray-300 accent-blue-600 cursor-pointer"
            />
          )}
        />
        <div className="flex-1">
          <Label htmlFor="isUrgent" className="text-base font-medium cursor-pointer">
            Notificação Urgente
          </Label>
          <p className="text-sm text-muted-foreground">
            Marque se esta notificação deve ser entregue com prioridade alta
          </p>
        </div>
      </div>

      <div className="flex justify-end gap-3 pt-4 border-t">
        <Button type="button" variant="outline" onClick={onDone}>Cancelar</Button>
        <Button type="submit" disabled={mutation.isPending} className="text-white">
          {mutation.isPending ? (
            <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Enviando...</>
          ) : (
            <><Send className="h-4 w-4 mr-2" />Enviar Notificação</>
          )}
        </Button>
      </div>
    </form>
  );
});

// ---- Page shell (no forms, no watch() calls here) ----

export const CreateNotification = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("general");
  const handleDone = () => navigate("/notifications");

  return (
    <>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={handleDone}
            className="h-10 w-10"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <HeaderPage
            title="Nova Notificação"
            description="Crie e envie notificações para os usuários do sistema."
          />
        </div>
      </div>

      <Card className="mt-6 bg-white">
        <CardContent className="p-4">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-3 mb-6">
              <TabsTrigger value="general" className="flex items-center gap-2">
                <Users className="h-4 w-4" />
                Geral
              </TabsTrigger>
              <TabsTrigger value="neighborhood" className="flex items-center gap-2">
                <MapPin className="h-4 w-4" />
                Bairro
              </TabsTrigger>
              <TabsTrigger value="user" className="flex items-center gap-2">
                <User className="h-4 w-4" />
                Usuário
              </TabsTrigger>
            </TabsList>

            <TabsContent value="general">
              <GeneralNotificationTab onDone={handleDone} />
            </TabsContent>

            <TabsContent value="neighborhood">
              <NeighborhoodNotificationTab onDone={handleDone} />
            </TabsContent>

            <TabsContent value="user">
              <UserNotificationTab onDone={handleDone} />
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </>
  );
};
